import { useState, useMemo, useEffect } from 'react';
import { X, Clock, TrendingUp, TrendingDown, AlertCircle, Tag } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';

interface SkillGroup {
  id: string;
  name: string;
  description?: string;
}

const CHANNELS = [
  { value: 'PHONE', label: 'Phone', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'CHAT', label: 'Chat', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'EMAIL', label: 'Email', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'SMS', label: 'SMS', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'SOCIAL', label: 'Social', color: 'bg-pink-100 text-pink-700 border-pink-200' },
] as const;

type ChannelType = typeof CHANNELS[number]['value'];

const DAY_OPTIONS = [
  { value: 0, label: 'Sun', fullLabel: 'Sunday' },
  { value: 1, label: 'Mon', fullLabel: 'Monday' },
  { value: 2, label: 'Tue', fullLabel: 'Tuesday' },
  { value: 3, label: 'Wed', fullLabel: 'Wednesday' },
  { value: 4, label: 'Thu', fullLabel: 'Thursday' },
  { value: 5, label: 'Fri', fullLabel: 'Friday' },
  { value: 6, label: 'Sat', fullLabel: 'Saturday' },
];

const PRESETS = [
  { label: 'Weekdays', days: [1, 2, 3, 4, 5] },
  { label: 'Weekends', days: [0, 6] },
  { label: 'Mon-Thu', days: [1, 2, 3, 4] },
  { label: 'Tue-Sat', days: [2, 3, 4, 5, 6] },
];

interface PlannedShift {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  requiredCount: number;
  assignedCount: number;
  status: 'PLANNED' | 'APPROVED' | 'SCHEDULED' | 'CANCELLED';
  channelAllocations?: ChannelAllocation[];
  skillGroupId?: string | null;
  skillGroup?: { id: string; name: string } | null;
}

interface ChannelAllocation {
  id: string;
  channel: 'PHONE' | 'CHAT' | 'EMAIL' | 'SMS' | 'SOCIAL';
  intervalStart: number;
  intervalEnd: number;
  allocatedAgents: number;
  isPrimary: boolean;
}

interface EditShiftModalProps {
  shift: PlannedShift;
  forecastId: string;
  isCreating?: boolean;
  onClose: () => void;
  onSave: (updatedShift: PlannedShift) => void;
}

export default function EditShiftModal({ shift, forecastId, isCreating = false, onClose, onSave }: EditShiftModalProps) {
  const token = useAuthStore((state) => state.token);
  const [startTime, setStartTime] = useState(formatTimeForInput(shift.startTime));
  const [endTime, setEndTime] = useState(formatTimeForInput(shift.endTime));
  const [agentsNeeded, setAgentsNeeded] = useState(shift.requiredCount);
  const [isSaving, setIsSaving] = useState(false);

  // Team/Skill Group selection
  const [skillGroups, setSkillGroups] = useState<SkillGroup[]>([]);
  const [selectedSkillGroupId, setSelectedSkillGroupId] = useState<string | null>(
    shift.skillGroupId || shift.skillGroup?.id || null
  );
  const [isLoadingSkillGroups, setIsLoadingSkillGroups] = useState(false);

  // Channel selection
  const [selectedChannels, setSelectedChannels] = useState<ChannelType[]>(() => {
    if (shift.channelAllocations && shift.channelAllocations.length > 0) {
      return [...new Set(shift.channelAllocations.map(a => a.channel))] as ChannelType[];
    }
    return [];
  });

  // Days of week (for creating new shifts)
  const [selectedDays, setSelectedDays] = useState<number[]>(() => {
    const date = new Date(shift.startTime);
    return [date.getDay()];
  });

  // Fetch skill groups for the organization
  useEffect(() => {
    const fetchSkillGroups = async () => {
      setIsLoadingSkillGroups(true);
      try {
        const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
        const response = await fetch(`${baseUrl}/skill-groups`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSkillGroups(data);
        }
      } catch (error) {
        console.error('Failed to fetch skill groups:', error);
      } finally {
        setIsLoadingSkillGroups(false);
      }
    };

    fetchSkillGroups();
  }, [token]);

  // Calculate duration
  const duration = useMemo(() => {
    if (!startTime || !endTime) return { hours: 0, display: '0h' };
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    let hours = endH - startH;
    let mins = endM - startM;
    if (mins < 0) {
      hours -= 1;
      mins += 60;
    }
    if (hours < 0) hours += 24;
    const totalHours = hours + mins / 60;
    return {
      hours: totalHours,
      display: mins > 0 ? `${hours}h ${mins}m` : `${hours}h`,
    };
  }, [startTime, endTime]);

  // Calculate impact estimates
  const impact = useMemo(() => {
    const originalAgents = shift.requiredCount;
    const agentChange = agentsNeeded - originalAgents;

    // Estimate occupancy impact: more agents = lower occupancy, fewer agents = higher occupancy
    // This is a simplified estimation - real calculation would use forecast data
    // Each agent change affects occupancy by ~2%
    const occupancyChangePerAgent = 2;
    const estimatedOccupancyChange = -agentChange * occupancyChangePerAgent;

    // Coverage hours impact
    const originalDuration = getDurationFromShift(shift);
    const newDuration = duration.hours;
    const hoursChange = (newDuration - originalDuration) * agentsNeeded;

    return {
      agentChange,
      occupancyChange: estimatedOccupancyChange,
      hoursChange: Math.round(hoursChange * 10) / 10,
      isPositive: agentChange > 0 || hoursChange > 0,
    };
  }, [agentsNeeded, duration.hours, shift]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

      // Get base date from original shift
      const originalDate = new Date(shift.startTime);
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);

      if (isCreating && selectedDays.length > 1) {
        // Create multiple shifts for selected days
        const createdShifts = [];

        for (const dayOfWeek of selectedDays) {
          // Calculate the date for this day of week
          const shiftDate = new Date(originalDate);
          const currentDay = shiftDate.getDay();
          let daysToAdd = dayOfWeek - currentDay;
          if (daysToAdd < 0) daysToAdd += 7;
          shiftDate.setDate(shiftDate.getDate() + daysToAdd);

          const shiftStartTime = new Date(shiftDate);
          shiftStartTime.setHours(startHours, startMinutes, 0, 0);

          const shiftEndTime = new Date(shiftDate);
          shiftEndTime.setHours(endHours, endMinutes, 0, 0);

          const shiftDurationIntervals = Math.ceil(duration.hours * 4);
          const channelAllocations = selectedChannels.length > 0
            ? selectedChannels.map((channel, idx) => ({
                channel,
                intervalStart: 0,
                intervalEnd: shiftDurationIntervals - 1,
                allocatedAgents: agentsNeeded / selectedChannels.length,
                isPrimary: idx === 0,
              }))
            : undefined;

          const response = await fetch(
            `${baseUrl}/forecasts/${forecastId}/shift-planning/shifts`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name: `Shift - ${DAY_OPTIONS[dayOfWeek].fullLabel}`,
                startTime: shiftStartTime.toISOString(),
                endTime: shiftEndTime.toISOString(),
                requiredCount: agentsNeeded,
                status: 'PLANNED',
                channelAllocations,
                skillGroupId: selectedSkillGroupId,
              }),
            }
          );

          if (response.ok) {
            const created = await response.json();
            createdShifts.push(created);
          }
        }

        if (createdShifts.length > 0) {
          onSave(createdShifts[0]);
          onClose();
        } else {
          throw new Error('Failed to create shifts');
        }
      } else {
        // Single shift create or update
        const newStartTime = new Date(originalDate);
        newStartTime.setHours(startHours, startMinutes, 0, 0);

        const newEndTime = new Date(originalDate);
        newEndTime.setHours(endHours, endMinutes, 0, 0);

        const shiftDurationIntervals = Math.ceil(duration.hours * 4);
        const channelAllocations = selectedChannels.length > 0
          ? selectedChannels.map((channel, idx) => ({
              channel,
              intervalStart: 0,
              intervalEnd: shiftDurationIntervals - 1,
              allocatedAgents: agentsNeeded / selectedChannels.length,
              isPrimary: idx === 0,
            }))
          : undefined;

        const url = isCreating
          ? `${baseUrl}/forecasts/${forecastId}/shift-planning/shifts`
          : `${baseUrl}/forecasts/${forecastId}/shift-planning/shifts/${shift.id}`;

        const response = await fetch(url, {
          method: isCreating ? 'POST' : 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: shift.name,
            startTime: newStartTime.toISOString(),
            endTime: newEndTime.toISOString(),
            requiredCount: agentsNeeded,
            status: shift.status,
            channelAllocations,
            skillGroupId: selectedSkillGroupId,
          }),
        });

        if (response.ok) {
          const updatedShift = await response.json();
          onSave(updatedShift);
          onClose();
        } else {
          throw new Error(`Failed to ${isCreating ? 'create' : 'update'} shift`);
        }
      }
    } catch (error) {
      console.error('Failed to save shift:', error);
      alert('Failed to save shift. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleChannel = (channel: ChannelType) => {
    if (selectedChannels.includes(channel)) {
      setSelectedChannels(selectedChannels.filter(c => c !== channel));
    } else {
      setSelectedChannels([...selectedChannels, channel]);
    }
  };

  const toggleDay = (day: number) => {
    if (selectedDays.includes(day)) {
      if (selectedDays.length > 1) {
        setSelectedDays(selectedDays.filter(d => d !== day));
      }
    } else {
      setSelectedDays([...selectedDays, day].sort((a, b) => a - b));
    }
  };

  const applyPreset = (days: number[]) => {
    setSelectedDays(days);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isCreating ? 'Add Shift' : 'Edit Shift'}
            </h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Configure shift details and see impact
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Time Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shift Time
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <span className="text-gray-400">to</span>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center mt-2 text-sm text-gray-500">
              <Clock className="h-4 w-4 mr-1" />
              <span>Duration: {duration.display}</span>
            </div>
          </div>

          {/* Agents Needed */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agents Needed
            </label>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => setAgentsNeeded(Math.max(1, agentsNeeded - 1))}
                className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 text-lg font-medium"
              >
                -
              </button>
              <input
                type="number"
                min="1"
                value={agentsNeeded}
                onChange={(e) => setAgentsNeeded(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setAgentsNeeded(agentsNeeded + 1)}
                className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 text-lg font-medium"
              >
                +
              </button>
            </div>
          </div>

          {/* Channels */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Channels
            </label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map((channel) => {
                const isSelected = selectedChannels.includes(channel.value);
                return (
                  <button
                    key={channel.value}
                    type="button"
                    onClick={() => toggleChannel(channel.value)}
                    className={`px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-all ${
                      isSelected
                        ? `${channel.color} border-current`
                        : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {channel.label}
                  </button>
                );
              })}
            </div>
            {selectedChannels.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                No channel selected - shift will cover all channels
              </p>
            )}
          </div>

          {/* Team / Skill Group */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-1.5">
                <Tag className="h-4 w-4" />
                Team
              </span>
            </label>
            {isLoadingSkillGroups ? (
              <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
            ) : (
              <select
                value={selectedSkillGroupId || ''}
                onChange={(e) => setSelectedSkillGroupId(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">No team assigned</option>
                {skillGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Assign this shift to a specific team or skill group
            </p>
          </div>

          {/* Days of Week (for creating) */}
          {isCreating && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days of Week
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {DAY_OPTIONS.map((day) => {
                  const isSelected = selectedDays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={`w-10 h-10 rounded-lg border-2 text-sm font-medium transition-all ${
                        isSelected
                          ? 'bg-purple-100 text-purple-700 border-purple-300'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => applyPreset(preset.days)}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Impact Preview */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <h3 className="font-medium text-gray-900">Estimated Impact</h3>
            </div>

            <div className="space-y-3">
              {/* Agent Change */}
              {impact.agentChange !== 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Agent Change</span>
                  <div className="flex items-center space-x-1">
                    {impact.agentChange > 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-600" />
                    )}
                    <span className={`font-medium ${impact.agentChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {impact.agentChange > 0 ? '+' : ''}{impact.agentChange}
                    </span>
                  </div>
                </div>
              )}

              {/* Occupancy Impact */}
              {impact.occupancyChange !== 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Occupancy Impact</span>
                  <div className="flex items-center space-x-1">
                    {impact.occupancyChange > 0 ? (
                      <TrendingUp className="h-4 w-4 text-amber-600" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-green-600" />
                    )}
                    <span className={`font-medium ${
                      impact.occupancyChange > 0 ? 'text-amber-600' : 'text-green-600'
                    }`}>
                      {impact.occupancyChange > 0 ? '+' : ''}{impact.occupancyChange.toFixed(0)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Coverage Hours */}
              {impact.hoursChange !== 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Coverage Hours</span>
                  <div className="flex items-center space-x-1">
                    {impact.hoursChange > 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-600" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-600" />
                    )}
                    <span className={`font-medium ${impact.hoursChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {impact.hoursChange > 0 ? '+' : ''}{impact.hoursChange}h
                    </span>
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="pt-2 border-t border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Overall</span>
                  <span className={`px-2 py-0.5 rounded text-sm font-medium ${
                    impact.isPositive
                      ? 'bg-green-100 text-green-700'
                      : impact.agentChange === 0 && impact.hoursChange === 0
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {impact.isPositive
                      ? 'Improves Coverage'
                      : impact.agentChange === 0 && impact.hoursChange === 0
                      ? 'No Change'
                      : 'Reduces Coverage'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {isCreating && selectedDays.length > 1 && (
              <span>Creating {selectedDays.length} shifts</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isSaving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{isSaving ? 'Saving...' : isCreating ? 'Create Shift' : 'Save Changes'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatTimeForInput(isoString: string | undefined | null): string {
  if (!isoString) return '09:00';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '09:00';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return '09:00';
  }
}

function getDurationFromShift(shift: { startTime: string; endTime: string }): number {
  try {
    const start = new Date(shift.startTime);
    const end = new Date(shift.endTime);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 8;
    return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  } catch {
    return 8;
  }
}
